import { List, Map, Set } from "immutable";

import { CONTENT_TYPE_SUBSCRIPTION_TIER_MAP } from "@seneca/course-service-consts";
import {
  ContentType,
  COURSE_SUBSCRIPTION_TIERS
} from "@seneca/course-service-types";

import { Assignment } from "seneca-common/features/assignments/state";
import CourseSectionsSpec from "seneca-common/features/assignments/state/models/spec/CourseSectionsSpec";
import * as assignmentSelectors from "seneca-common/features/assignments/state/selectors/state/assignments/assignment";
import { getSectionContentIds } from "seneca-common/features/assignments/state/selectors/state/assignments/assignment/spec/courseSectionsSpec";
import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import { SectionContent } from "features/assignments/selectors/getAssignmentSectionIdsInCourseOrder";
import { SELF_MARKING_DONT_KNOW_MARK } from "features/exam-questions/consts";

import ContentStat, { ContentStatType } from "../../../../models/ContentStat";
import QuestionStat from "../../../../models/QuestionStat";
import QuizDetails from "../../../../models/QuizDetails";
import QuizStat from "../../../../models/QuizStat";
import QuizModuleStat from "../../../../models/QuizStat/QuizModuleStat";
import SectionStat, { SectionStatType } from "../../../../models/SectionStat";
import StudentAssignment from "../../../../models/StudentAssignment";
import { VocabularyWordStatType } from "../../../../models/VocabularyWordStat";
import * as questionStatSelectors from "./questionStat";
import * as quizStatSelectors from "./quizStats";
import * as quizModuleStatSelectors from "./quizStats/quizModuleStats";
import * as sectionStatSelectors from "./sectionStat";
import * as vocabularySelectors from "./vocabularyWordStats";

type SectionStatsType = Map<string, SectionStat>;
type ContentStatsType = Map<string, Map<string, ContentStat>>;
type QuestionStatsType = Map<string, QuestionStat>;
type QuizStatsType = Map<string, QuizStat>;
type QuizModuleStatsType = Map<string, QuizModuleStat>;
type QuizDetailsType = Map<string, QuizDetails>;
type VocabularyStatsType = Map<string, VocabularyWordStatType>;

// Defined as a const outside of selectors to allow memoisation to work
// We lose type safety here, so always specify the return type on any function that returns this
const emptyMap = Map<string, any>();

function getAssignmentSectionStats(
  assignment: StudentAssignment
): SectionStatsType {
  return assignment.sectionStats || emptyMap;
}

function getAssignmentContentStats(
  assignment: StudentAssignment
): ContentStatsType {
  return assignment.contentStats || emptyMap;
}

export function getAssignmentQuestionStats(
  assignment: StudentAssignment
): QuestionStatsType {
  return assignment.questionStats || emptyMap;
}

export function getAssignmentQuizStats(
  assignment: StudentAssignment
): QuizStatsType {
  return assignment.quizStats || emptyMap;
}

export function getAssignmentWordStats(
  assignment: StudentAssignment
): VocabularyStatsType {
  return assignment.vocabularyStats || emptyMap;
}

export function getAssignmentQuizDetails(
  assignment: StudentAssignment
): QuizDetailsType {
  return assignment.quizDetails || emptyMap;
}

export function getAssignmentTeacherName(
  assignment: StudentAssignment
): string | undefined {
  return assignment.teacherName;
}

export function getAssignmentAggregatedQuestionsMarks(
  assignment: StudentAssignment
) {
  const stats = getAssignmentQuestionStats(assignment);
  return stats.reduce(
    (acc, stat) => {
      if (
        questionStatSelectors.getQuestionMark(stat) ===
        SELF_MARKING_DONT_KNOW_MARK
      )
        return acc;

      acc.marksTotal += questionStatSelectors.getQuestionMarksTotal(stat) ?? 0;
      acc.marksReceived += questionStatSelectors.getQuestionMark(stat) ?? 0;
      return acc;
    },
    {
      marksTotal: 0,
      marksReceived: 0
    }
  );
}

export function getAssignmentQuizBestMarks(
  assignment: StudentAssignment,
  quizId: string
) {
  const moduleStats = getAssignmentQuizModuleStats(assignment, quizId);
  return moduleStats.reduce(
    (acc, stat) =>
      acc + (quizModuleStatSelectors.getQuizModuleBestMark(stat) ?? 0),
    0
  );
}

export function getAssignmentQuizModulesAverageBestMarks(
  assignment: StudentAssignment,
  quizId: string
) {
  const moduleStats = getAssignmentQuizModuleStats(assignment, quizId);

  const { totalBestMarks, marksTotal } = moduleStats.reduce(
    (acc, stat) => {
      const bestMark = quizModuleStatSelectors.getQuizModuleBestMark(stat);
      const marksTotal =
        quizModuleStatSelectors.getQuizModuleMarksTotal(stat) ?? 0;

      if (bestMark || bestMark === 0) {
        return {
          totalBestMarks: acc.totalBestMarks + bestMark,
          marksTotal: acc.marksTotal + marksTotal
        };
      }
      return acc;
    },
    { totalBestMarks: 0, marksTotal: 0 }
  );

  if (marksTotal === 0) return 0;

  return totalBestMarks / marksTotal;
}

export function getAssignmentUserId(
  assignment: StudentAssignment
): string | undefined {
  return assignment.userId;
}

function getSectionStat(sectionStats: SectionStatsType, sectionId: string) {
  return sectionStats.get(sectionId);
}

function getContentStat(
  contentStats: ContentStatsType,
  sectionId: string,
  contentType: ContentType
) {
  return contentStats.get(sectionId)?.get(contentType);
}

function getQuestionStat(questionStats: QuestionStatsType, questionId: string) {
  return questionStats.get(questionId);
}

function getQuizStat(quizStats: QuizStatsType, quizId: string) {
  return quizStats.get(quizId);
}

function getQuizModuleStat(
  quizModuleStats: QuizModuleStatsType,
  moduleId: string
) {
  return quizModuleStats.get(moduleId);
}

type SectionId = string;
export const getAssignmentSectionStat: (
  arg0: StudentAssignment,
  arg1: SectionId
) => SectionStatType | null | undefined = composeSelector(
  getAssignmentSectionStats,
  getSectionStat
);

export const getAssignmentContentStat: (
  state: StudentAssignment,
  sectionId: string,
  contentType: ContentType
) => ContentStatType | undefined = composeSelector(
  getAssignmentContentStats,
  getContentStat
);

export const getAssignmentQuizStat: (
  state: StudentAssignment,
  quizId: string
) => QuizStat | undefined = composeSelector(
  getAssignmentQuizStats,
  getQuizStat
);

export function getAssignmentQuizModuleStats(
  state: StudentAssignment,
  quizId: string
): QuizModuleStatsType {
  return (
    quizStatSelectors.getQuizModuleStats(
      getAssignmentQuizStat(state, quizId)
    ) || emptyMap
  );
}

export function getAssignmentQuizModuleStat(
  state: StudentAssignment,
  quizId: string,
  moduleId: string
) {
  const quizModuleStats = getAssignmentQuizModuleStats(state, quizId);
  return quizModuleStats && getQuizModuleStat(quizModuleStats, moduleId);
}

export const getAssignmentQuestionStat: (
  state: StudentAssignment,
  questionId: string
) => QuestionStat | undefined = composeSelector(
  getAssignmentQuestionStats,
  getQuestionStat
);

export function getAssignmentQuestionMark(
  assignment: StudentAssignment,
  questionId: string
) {
  return questionStatSelectors.getQuestionMark(
    getAssignmentQuestionStat(assignment, questionId)
  );
}

export function getAssignmentQuizMarksTotal(
  assignment: StudentAssignment,
  quizId: string
) {
  return quizStatSelectors.getQuizMarksTotal(
    getAssignmentQuizStat(assignment, quizId)
  );
}

export function getAssignmentQuestionMarksTotal(
  assignment: StudentAssignment,
  questionId: string
) {
  return questionStatSelectors.getQuestionMarksTotal(
    getAssignmentQuestionStat(assignment, questionId)
  );
}

export function isAssignmentQuestionFlagged(
  assignment: StudentAssignment,
  questionId: string
) {
  return questionStatSelectors.isQuestionFlagged(
    getAssignmentQuestionStat(assignment, questionId)
  );
}

export function getAssignmentSectionBestScore(
  assignment: StudentAssignment,
  sectionId: string,
  contentType?: ContentType
): number | null | undefined {
  const stat = getAssignmentContentOrSectionStat(
    assignment,
    sectionId,
    contentType
  );
  return stat && sectionStatSelectors.getBestScore(stat);
}

export function getAssignmentSectionTotalStudyTime(
  assignment: StudentAssignment,
  sectionId: string,
  contentType?: ContentType
): number | null | undefined {
  const stat = getAssignmentContentOrSectionStat(
    assignment,
    sectionId,
    contentType
  );
  return stat && sectionStatSelectors.getTotalStudyTime(stat);
}

function getAssignmentContentOrSectionStat(
  assignment: StudentAssignment,
  sectionId: string,
  contentType?: ContentType
) {
  const spec = assignmentSelectors.getSpec(assignment as Assignment);
  const sectionContentIds = getSectionContentIds(spec as CourseSectionsSpec);

  if (sectionContentIds && sectionContentIds.size > 0 && contentType) {
    return getAssignmentContentStat(assignment, sectionId, contentType);
  }

  return getAssignmentSectionStat(assignment, sectionId);
}

export function getAssignmentQuizTotalStudyTime(
  assignment: StudentAssignment,
  quizId: string
): number | null | undefined {
  const quizStat = getAssignmentQuizStat(assignment, quizId);
  return quizStat && quizStatSelectors.getQuizTotalStudyTime(quizStat);
}

export function getAssignmentQuestionStudyTime(
  assignment: StudentAssignment,
  questionId: string
): number | null | undefined {
  const questionStat = getAssignmentQuestionStat(assignment, questionId);
  return (
    questionStat && questionStatSelectors.getQuestionStudyTime(questionStat)
  );
}

export function getAssignmentTotalStudyTime(
  assignment: StudentAssignment
): number {
  // TODO: get from BE from aggregated stats when we return it
  const studiedSectionIds = getStudiedSectionIds(assignment);
  const sectionTimes = studiedSectionIds.map(sectionId =>
    getAssignmentSectionTotalStudyTime(assignment, sectionId)
  );
  const studiedQuestionIds = getStudiedQuestionIds(assignment);
  const questionTimes = studiedQuestionIds.map(questionId =>
    getAssignmentQuestionStudyTime(assignment, questionId)
  );
  const studiedQuizIds = getStudiedQuizIds(assignment);
  const quizTimes = studiedQuizIds.map(quizId =>
    getAssignmentQuizTotalStudyTime(assignment, quizId)
  );

  const studiedWordIds = getStudiedWordIds(assignment);
  const wordTimes = studiedWordIds.map(
    wordId => getAssignmentWordStats(assignment).get(wordId)?.totalStudyTime
  );

  return [...sectionTimes, ...questionTimes, ...quizTimes, ...wordTimes].reduce(
    (acc: number, val) => {
      if (!val) return acc;
      return acc + val;
    },
    0
  );
}

export function getNumberOfSectionsStudied(assignment: StudentAssignment) {
  const spec = assignmentSelectors.getSpec(assignment as Assignment);
  const sectionContentIds = getSectionContentIds(spec as CourseSectionsSpec);

  if (sectionContentIds && sectionContentIds.size > 0) {
    const contentStats = getAssignmentContentStats(assignment);

    return contentStats.valueSeq().reduce((acc, contentStats) => {
      return acc + (contentStats.keySeq()?.size ?? 0);
    }, 0);
  }

  const sectionStats = getAssignmentSectionStats(assignment);

  return sectionStats.size;
}

export function getNumberOfItemsAssigned(assignment: StudentAssignment) {
  const numOfSectionsAssigned =
    assignmentSelectors.getAssignmentNumberOfSections(assignment as Assignment);
  const numOfQuestionsAssigned = getAssignmentExamQuestionIds(assignment).size;
  const numOfQuizzesAssigned = getAssignmentQuizIds(assignment).size;
  const numOfWordsAssigned = getAssignmentWordIds(assignment).size;

  return (
    numOfSectionsAssigned +
    numOfQuestionsAssigned +
    numOfQuizzesAssigned +
    numOfWordsAssigned
  );
}

export function getNumberOfSessionsAssigned(assignment: StudentAssignment) {
  const numOfSectionsAssigned =
    assignmentSelectors.getAssignmentNumberOfSections(assignment as Assignment);
  const numOfQuizzesAssigned = getAssignmentQuizIds(assignment).size;
  const includesExamQuestions = assignmentIncludesExamQuestions(assignment);
  const numOfWordsAssigned = getAssignmentWordIds(assignment).size;

  return (
    numOfSectionsAssigned +
    (includesExamQuestions ? 1 : 0) +
    numOfQuizzesAssigned +
    numOfWordsAssigned
  );
}

export function getNumberOfItemsStudied(assignment: StudentAssignment) {
  const numOfStudiedSections = getNumberOfSectionsStudied(assignment);
  const numOfStudiedQuestions = getAssignmentQuestionStats(assignment).size;
  const numOfStudiedQuizzes = getAssignmentQuizStats(assignment).size;
  const numOfStudiedWords = getAssignmentVocabularyStats(assignment).size;

  return (
    numOfStudiedSections +
    numOfStudiedQuestions +
    numOfStudiedQuizzes +
    numOfStudiedWords
  );
}

export function getNumberOfSessionsStudied(assignment: StudentAssignment) {
  const numOfSectionsStudied = getNumberOfSectionsStudied(assignment);
  const allQuestionsStudied = allExamQuestionsCompleted(assignment);
  const numOfQuizzesStudied = getAssignmentQuizStats(assignment).size;
  const numOfWordsStudied = getAssignmentVocabularyStats(assignment).size;

  return (
    numOfSectionsStudied +
    (allQuestionsStudied ? 1 : 0) +
    numOfQuizzesStudied +
    numOfWordsStudied
  );
}

export function getNumberOfUnstudiedItems(assignment: StudentAssignment) {
  const numberOfStudiedItems = getNumberOfItemsStudied(assignment);
  const numberOfAssignedItems = getNumberOfItemsAssigned(assignment);
  return numberOfAssignedItems - numberOfStudiedItems;
}

export function getNumberOfUnstudiedSections(assignment: StudentAssignment) {
  const numOfSectionsAssigned =
    assignmentSelectors.getAssignmentNumberOfSections(assignment as Assignment);
  const numOfStudiedSections = getNumberOfSectionsStudied(assignment);

  return numOfSectionsAssigned - numOfStudiedSections;
}

export function getAssignmentProgress(assignment: StudentAssignment): number {
  const numOfSessionsAssigned = getNumberOfSessionsAssigned(assignment);
  const numberOfSessionsStudied = getNumberOfSessionsStudied(assignment);

  return numOfSessionsAssigned > 0
    ? (numberOfSessionsStudied / numOfSessionsAssigned) * 100
    : 0;
}

export function getStudiedSectionIds(
  assignment: StudentAssignment
): List<string> {
  return getStudiedSectionContents(assignment)
    .map(sectionContent => sectionContent.sectionId)
    .toSet()
    .toList();
}

export function getStudiedSectionContents(
  assignment: StudentAssignment
): List<SectionContent> {
  const contentStats = getAssignmentContentStats(assignment);
  const spec = assignmentSelectors.getSpec(assignment as Assignment);
  const sectionContentIds = getSectionContentIds(spec as CourseSectionsSpec);

  if (sectionContentIds && sectionContentIds.size > 0) {
    return contentStats.entrySeq().reduce((acc, [sectionId, contentStats]) => {
      contentStats.keySeq().forEach(contentType => {
        acc = acc.push({
          sectionId,
          contentType: contentType as ContentType
        });
      });

      return acc;
    }, List());
  }

  const sectionStats = getAssignmentSectionStats(assignment);

  return sectionStats.keySeq().reduce((acc, sectionId) => {
    return acc.push({ sectionId, contentType: ContentType.standard });
  }, List());
}

export function getStudiedQuestionIds(
  assignment: StudentAssignment
): List<string> {
  return getAssignmentQuestionStats(assignment).keySeq().toList();
}

export function getStudiedQuizIds(assignment: StudentAssignment): List<string> {
  return getAssignmentQuizStats(assignment).keySeq().toList();
}

export function getStudiedWordIds(assignment: StudentAssignment): List<string> {
  return getAssignmentWordStats(assignment).keySeq().toList();
}

export function getNumberOfQuestionsCompleted(
  assignment: StudentAssignment
): number {
  return getStudiedQuestionIds(assignment).size;
}

export function allExamQuestionsCompleted(assignment: StudentAssignment) {
  const assignedQuestionIds = getAssignmentExamQuestionIds(assignment);
  if (assignedQuestionIds.size === 0) return false;
  const completedQuestionIds = getStudiedQuestionIds(assignment);
  return (
    Set(completedQuestionIds).intersect(Set(assignedQuestionIds)).size ===
    assignedQuestionIds.size
  );
}

export function allSectionsCompleted(assignment: StudentAssignment) {
  const assignedSectionIds = assignmentSelectors.getAssignmentSectionIds(
    assignment as Assignment
  );
  const completedSectionIds = getStudiedSectionIds(assignment);
  return (
    Set(completedSectionIds).intersect(Set(assignedSectionIds)).size ===
    assignedSectionIds.size
  );
}

export function allContentsCompleted(assignment: StudentAssignment) {
  const { contentTypesBySectionId } = assignment;
  const studiedSectionContents = getStudiedSectionContents(assignment);

  return contentTypesBySectionId
    ?.entrySeq()
    .every(([sectionId, contentTypes]) => {
      const studiedSectionContent = studiedSectionContents
        .filter(sectionContent => sectionContent.sectionId === sectionId)
        .map(sectionContent => sectionContent.contentType);

      return contentTypes.every(contentType =>
        studiedSectionContent.includes(contentType)
      );
    });
}

export function allFreeContentsCompleted(assignment: StudentAssignment) {
  const { contentTypesBySectionId } = assignment;
  const studiedSectionContents = getStudiedSectionContents(assignment);

  return contentTypesBySectionId
    ?.entrySeq()
    .every(([sectionId, contentTypes]) => {
      const studiedSectionContent = studiedSectionContents
        .filter(sectionContent => sectionContent.sectionId === sectionId)
        .map(sectionContent => sectionContent.contentType);

      const courseSectionFreeContent = contentTypes.filter(contentType => {
        return (
          CONTENT_TYPE_SUBSCRIPTION_TIER_MAP[contentType] ===
          COURSE_SUBSCRIPTION_TIERS.free
        );
      });

      return courseSectionFreeContent.every(contentType => {
        return studiedSectionContent.includes(contentType);
      });
    });
}

export function getHasContentSpec(assignment: StudentAssignment) {
  return !!assignment.spec?.get("sectionContentIds", undefined);
}

export function allQuizzesCompleted(assignment: StudentAssignment) {
  const assignedQuizIds = assignmentSelectors.getAssignmentQuizIds(
    assignment as Assignment
  );
  const completedQuizIds = getStudiedQuizIds(assignment);
  return (
    Set(completedQuizIds).intersect(Set(assignedQuizIds)).size ===
    assignedQuizIds.size
  );
}

export function allWordsCompleted(assignment: StudentAssignment) {
  const assignedWordIds = assignmentSelectors.getAssignmentWordIds(
    assignment as Assignment
  );
  const completedWordIds = getStudiedWordIds(assignment);
  return (
    Set(completedWordIds).intersect(Set(assignedWordIds)).size ===
    assignedWordIds.size
  );
}

export function sectionHasBeenStudied(
  assignment: StudentAssignment,
  sectionId: string
): boolean {
  return getStudiedSectionIds(assignment).includes(sectionId);
}

export function sectionContentHasBeenStudied(
  assignment: StudentAssignment,
  sectionId: string,
  contentType: ContentType
): boolean {
  return getStudiedSectionContents(assignment).some(
    content =>
      content.sectionId === sectionId && content.contentType === contentType
  );
}

export function getNumberOfSectionsCompleted(
  assignment: StudentAssignment
): number {
  return getStudiedSectionIds(assignment).size;
}

export function getAssignmentExamQuestionIds(
  assignment: StudentAssignment
): List<string> {
  return assignmentSelectors.getAssignmentExamQuestionIds(
    assignment as Assignment
  );
}

export function getAssignmentQuizIds(
  assignment: StudentAssignment
): List<string> {
  return assignmentSelectors.getAssignmentQuizIds(assignment as Assignment);
}

export function getAssignmentWordIds(
  assignment: StudentAssignment
): List<string> {
  return assignmentSelectors.getAssignmentWordIds(assignment as Assignment);
}

export function assignmentIncludesExamQuestions(assignment: StudentAssignment) {
  return assignmentSelectors.assignmentIncludesExamQuestions(
    assignment as Assignment
  );
}

export function getAssignmentContentTypesBySectionId(
  assignment: StudentAssignment
) {
  return assignment.contentTypesBySectionId;
}

export function isLastSectionToFinishAssignment(
  assignment: StudentAssignment,
  sectionId: string,
  hasHardestQuestionContent: boolean
) {
  const spec = assignmentSelectors.getSpec(assignment as Assignment);
  const sectionContentIds = getSectionContentIds(spec as CourseSectionsSpec);
  const contentTypesBySectionId =
    getAssignmentContentTypesBySectionId(assignment) ?? Map();

  if (sectionContentIds && sectionContentIds.size > 0) {
    const contentStats = getAssignmentContentStats(assignment);
    const contentType = hasHardestQuestionContent
      ? ContentType.hardestQuestions
      : ContentType.standard;

    const numberOfContentsLeftToStudy =
      contentTypesBySectionId.valueSeq().flatten().toList().size -
      contentStats?.valueSeq().flatten().toList().size;

    return (
      numberOfContentsLeftToStudy === 1 &&
      !contentStats.get(sectionId)?.get(contentType)
    );
  }

  const assignedSectionIds = assignmentSelectors.getAssignmentSectionIds(
    assignment as Assignment
  );
  const studiedSectionIds = getStudiedSectionIds(assignment);

  const unstudiedSectionIds: List<string> = assignedSectionIds.filter(
    (id: any) => !studiedSectionIds.contains(id)
  );
  const numberOfSectionsLeftToStudy: number = unstudiedSectionIds.size;

  return (
    numberOfSectionsLeftToStudy === 1 &&
    unstudiedSectionIds.get(0) === sectionId
  );
}

export function isLastQuizToFinishAssignment(
  assignment: StudentAssignment,
  quizId: string
) {
  const assignedQuizIds = assignmentSelectors.getAssignmentQuizIds(
    assignment as Assignment
  );
  const studiedQuizIds = getStudiedQuizIds(assignment);
  const unstudiedQuizIds = assignedQuizIds.filter(
    (id: any) => !studiedQuizIds.contains(id)
  );
  const numberOfQuizzesLeftToStudy = unstudiedQuizIds.size;
  return numberOfQuizzesLeftToStudy === 1 && unstudiedQuizIds.get(0) === quizId;
}

export function isLastWordToFinishAssignment(
  assignment: StudentAssignment,
  wordId: string
) {
  const assignedWordIds = assignmentSelectors.getAssignmentWordIds(
    assignment as Assignment
  );
  const studiedWordIds = getStudiedWordIds(assignment);
  const unstudiedWordIds = assignedWordIds.filter(
    (id: any) => !studiedWordIds.contains(id)
  );
  const numberOfWordsLeftToStudy = unstudiedWordIds.size;
  return numberOfWordsLeftToStudy === 1 && unstudiedWordIds.get(0) === wordId;
}

export function getAssignmentQuizModulesWithTitles(
  assignment: StudentAssignment,
  quizId: string
) {
  const quizDetails = getAssignmentQuizDetails(assignment).get(quizId);
  const quizStats = getAssignmentQuizStats(assignment).get(quizId);
  return quizDetails?.modules?.mapEntries(
    ([moduleId, module]): [
      string,
      {
        moduleTitle: string | undefined;
        moduleStat: QuizModuleStat | undefined;
      }
    ] => {
      const moduleStat = quizStats?.moduleStats?.get(moduleId);
      return [
        moduleId,
        {
          moduleTitle: module.moduleTitle,
          moduleStat
        }
      ];
    }
  );
}

export function getAssignmentVocabularyStats(
  assignment: StudentAssignment
): VocabularyStatsType {
  return assignment.vocabularyStats || emptyMap;
}

export function getAssignmentWordBestScore(
  assignment: StudentAssignment,
  wordId: string
) {
  const wordStats = getAssignmentVocabularyStats(assignment).get(wordId);
  return wordStats && vocabularySelectors.getBestScore(wordStats);
}

export function wordHasBeenStudied(
  assignment: StudentAssignment,
  wordId: string
) {
  return getAssignmentVocabularyStats(assignment).has(wordId);
}

export {
  getAssignmentContentIds as getContentIds,
  getAssignmentContentIdsForSection as getContentIdsForSection,
  getAssignmentCourseId as getCourseId,
  getAssignmentId,
  getAssignmentNumberOfSections as getNumberOfSections,
  getAssignmentQuizIds as getQuizIds,
  getAssignmentSectionIds as getSectionIds,
  getAssignmentWordIds as getWordIds,
  getClassId,
  getDueDate,
  getName,
  getSpec,
  getStartDate,
  isQuizzesOnly
} from "seneca-common/features/assignments/state/selectors/state/assignments/assignment";
